import { Link } from "wouter";
import { MessageCircle, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { normalizeImageUrl } from "@/lib/image-url";

interface Chat {
  id: string;
  listing_id: string;
  listing_title?: string;
  seller_id: string;
  buyer_id: string;
  seller_name?: string;
  buyer_name?: string;
  seller_avatar?: string;
  buyer_avatar?: string;
  listing_images?: string[];
  listing_price_cents?: number | null;
  last_msg_at: number;
  last_message?: string;
  unread_count?: number;
}

interface ChatListProps {
  chats: Chat[];
  currentUserId: string;
}

export function ChatList({ chats, currentUserId }: ChatListProps) {
  if (chats.length === 0) {
    return (
      <div className="text-center text-ink-3 text-sm py-12">
        <MessageCircle className="w-12 h-12 mx-auto mb-4 text-ink-4" />
        <p>채팅 내역이 없습니다</p>
        <p className="text-xs mt-1">새로운 거래를 시작해보세요!</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {chats.map((chat) => {
        const isSeller = chat.seller_id === currentUserId;
        const otherUserName = isSeller ? chat.buyer_name : chat.seller_name;
        const otherUserAvatar = isSeller ? chat.buyer_avatar : chat.seller_avatar;
        const timeAgo = formatDistanceToNow(new Date(chat.last_msg_at), {
          addSuffix: true,
          locale: ko,
        });

        const formatPrice = (cents: number | null | undefined) => {
          if (cents === null || cents === undefined) return null;
          if (cents === 0) return "무료나눔";
          return `$${(cents / 100).toFixed(0)}`;
        };

        return (
          <Link key={chat.id} href={`/chat/${chat.id}`}>
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  {/* Thumbnail */}
                  <div className="shrink-0">
                    {chat.listing_images && chat.listing_images.length > 0 ? (
                      <img
                        src={normalizeImageUrl(chat.listing_images[0]) || ''}
                        alt={chat.listing_title || "상품"}
                        className="w-16 h-16 rounded-lg object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23e5e7eb" width="64" height="64"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="10" dy="35" dx="32" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                        <MessageCircle className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-base truncate mb-1">
                          {chat.listing_title || "상품"}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{otherUserName || "알 수 없음"}</span>
                          {chat.listing_price_cents !== null && chat.listing_price_cents !== undefined && (
                            <>
                              <span>·</span>
                              <span className="font-semibold text-foreground">
                                {formatPrice(chat.listing_price_cents)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo}
                        </div>
                        {chat.unread_count && chat.unread_count > 0 && (
                          <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs">
                            {chat.unread_count > 9 ? "9+" : chat.unread_count}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {chat.last_message && (
                      <div className="text-sm text-muted-foreground line-clamp-1 mt-1">
                        {chat.last_message}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
